import React from "react";
import {
	Box,
	Typography,
	List,
	ListItemButton,
	ListItemAvatar,
	Avatar,
	ListItemText,
	Badge,
	Popover,
	Divider,
	IconButton,
	Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import PersonAddIcon from "@mui/icons-material/PersonAddOutlined";
import GroupAddIcon from "@mui/icons-material/GroupAddOutlined";
import GroupIcon from "@mui/icons-material/Groups";
import { Conversation } from "@/types/chat";

interface ChatPopoverProps {
	anchorEl: HTMLElement | null;
	onClose: () => void;
	conversations: Conversation[];
	onOpenConversation: (id: string) => void;
	onNewChat: () => void;
	onNewGroupChat: () => void;
	currentUserId?: string;
}

export const ChatPopover: React.FC<ChatPopoverProps> = ({
	anchorEl,
	onClose,
	conversations,
	onOpenConversation,
	onNewChat,
	onNewGroupChat,
	currentUserId,
}) => {
	const theme = useTheme();
	const open = Boolean(anchorEl);

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "right",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "right",
			}}
			slotProps={{
				paper: {
					sx: {
						width: 360,
						maxHeight: 500,
						borderRadius: 3,
						mt: 1.5,
						boxShadow: theme.shadows[10],
						backgroundColor: "background.paper",
						backdropFilter: "blur(20px)", // Glassmorphism effect
						display: "flex",
						flexDirection: "column",
					},
				},
			}}
		>
			<Box
				sx={{
					p: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="h6" fontWeight="600">
					Чат
				</Typography>
				<Box>
					<Tooltip title="Шинэ групп үүсгэх">
						<IconButton size="small" onClick={onNewGroupChat} sx={{ mr: 1, backgroundColor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)" }}>
							<GroupAddIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Шинэ чат нээх">
						<IconButton size="small" onClick={onNewChat} sx={{ backgroundColor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)" }}>
							<PersonAddIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>
			<Divider />
			<List
				sx={{
					p: 0,
					flexGrow: 1,
					overflowY: "auto",
					"&::-webkit-scrollbar": { width: 6 },
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
						borderRadius: 3,
						"&:hover": {
							backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
						},
					},
				}}
			>
				{conversations.length === 0 ? (
					<Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
						<Typography variant="body2">Хоосон байна</Typography>
					</Box>
				) : (
					conversations.map((conv) => {
						let displayName = conv.name || "Unknown";
						let displayAvatar = "";
						let isOnline = false;
						let isGroup = conv.type === "GROUP";

						if (isGroup) {
							displayName = conv.name || "Group Chat";
						} else if (conv.participants?.length > 0) {
							const otherUser = conv.participants.find(p => p.id !== currentUserId) || conv.participants[0];
							const fullName = `${otherUser.firstName || ""} ${otherUser.lastName || ""}`.trim();
							displayName = fullName || otherUser.username || "Unknown";
							displayAvatar = otherUser.avatar || "";
							isOnline = Boolean((otherUser as any).isOnline);
						}

						const lastMsg = conv.messages && conv.messages.length > 0 ? conv.messages[conv.messages.length - 1] : null;
						let latestMessageText = "Зурвас алга";
						if (lastMsg) {
							if (lastMsg.content) {
								const match = lastMsg.content.match(/^\$\$SHARE:(CUSTOMER|CONTRACT|INVOICE):(.+)\$\$/);
								if (match) {
									const type = match[1];
									if (type === "CUSTOMER") latestMessageText = "Харилцагч хуваалцав";
									else if (type === "CONTRACT") latestMessageText = "Гэрээ хуваалцав";
									else if (type === "INVOICE") latestMessageText = "Нэхэмжлэх хуваалцав";
								} else {
									latestMessageText = lastMsg.content;
								}
							} else if (lastMsg.attachments && lastMsg.attachments.length > 0) {
								const att = lastMsg.attachments[0];
								if (att.fileType.startsWith("image/")) {
									latestMessageText = "📷 Зураг илгээв";
								} else {
									latestMessageText = "📎 Файл илгээв";
								}
							}
						}

						return (
							<ListItemButton
								key={conv.id}
								onClick={() => {
									onOpenConversation(conv.id);
									onClose();
								}}
								sx={{
									borderBottom: `1px solid ${theme.palette.divider}`,
									"&:last-child": { borderBottom: 0 },
									transition: "background-color 0.2s ease",
									"&:hover": {
										backgroundColor: "action.hover",
									},
								}}
							>
								<ListItemAvatar sx={{ mr: 2, position: "relative" }}>
									{isGroup ? (
										<Avatar
											sx={{
												width: 44,
												height: 44,
												bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.1)" : "primary.light",
												color: theme.palette.mode === 'dark' ? "#fff" : "primary.main",
											}}
										>
											<GroupIcon />
										</Avatar>
									) : (
										<Badge
											color="success"
											variant="dot"
											invisible={!isOnline}
											anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
											overlap="circular"
											sx={{
												"& .MuiBadge-badge": {
													border: `2px solid ${theme.palette.background.paper}`,
													width: 10,
													height: 10,
													borderRadius: "50%",
												}
											}}
										>
											<Avatar 
												src={displayAvatar} 
												alt={displayName}
												sx={{
													width: 44,
													height: 44,
													border: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
												}} 
											/>
										</Badge>
									)}
								</ListItemAvatar>
								<ListItemText
									primary={
										<Typography
											variant="subtitle2"
											noWrap
											sx={{ fontWeight: conv.unreadCount ? 700 : 500 }}
										>
											{displayName}
										</Typography>
									}
									secondary={
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mt: 0.5,
											}}
										>
											<Typography
												variant="body2"
												color="text.secondary"
												noWrap
												sx={{
													maxWidth: "180px",
													fontWeight: conv.unreadCount ? 600 : 400,
													color: conv.unreadCount
														? "text.primary"
														: "text.secondary",
												}}
											>
												{latestMessageText}
											</Typography>
											{conv.lastMessageAt && (
												<Typography 
													variant="caption" 
													color="text.secondary"
													sx={{ flexShrink: 0, ml: 1, whiteSpace: "nowrap" }}
												>
													{moment(conv.lastMessageAt).fromNow(true)}
												</Typography>
											)}
										</Box>
									}
								/>
							</ListItemButton>
						);
					})
				)}
			</List>
			<Divider />
			<Box
				sx={{
					p: 1.5,
					textAlign: "center",
					background: theme.palette.mode === "dark"
						? "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)"
						: "linear-gradient(180deg, rgba(0,0,0,0.01) 0%, rgba(0,0,0,0.03) 100%)",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography
					variant="caption"
					sx={{
						fontWeight: "800",
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
						WebkitBackgroundClip: "text",
						WebkitTextFillColor: "transparent",
						fontSize: "0.65rem",
					}}
				>
					POWERED BY SAAS
				</Typography>
			</Box>
		</Popover>
	);
};
